import { useApolloClient } from '@apollo/client';
import { Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSessionStorage } from 'react-use';

import ConversionToggle from '@components/ConversionToggle';
import type {
  DataTableRecord,
  DataTableType,
} from '@components/DataTable/DataTable';
import { TableTitle } from '@components/DataTable/DataTable.style';
import FilterButton from '@components/FilterButton';
import { ProcessStepper } from '@components/ProcessStepper/index';

import { LAB_TYPES } from '@constants/option-types/lab';

import { useSession } from '@hooks/useSession';
import { useThrottledLoader } from '@hooks/useThrottledLoader';

import { NoRecordsWrapper } from '@layouts/Layouts/BigTitleLayout';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';

import type {
  GetLabResultResourcesQuery,
  GetLabResultResourcesQueryVariables,
} from 'generated/diagnostic-report.generated';
import { GetLabResultResourcesDocument } from 'generated/diagnostic-report.generated';
import type { GetRecordInput } from 'generated/types';

import LabResultsSection from './LabResultsSection';

const messages = defineMessages({
  noRecordsFound: {
    id: 'labResultsMain.noRecordsFound',
    defaultMessage: 'No Known Lab Results',
  },
});

const LabResultCardView: React.FC<{
  data: DiagnosticReportRecord[];
  displayData: DiagnosticReportRecord[];
  tableData: DataTableRecord[];
  records: LabResultSectionRecord[];
  onChange: (filteredItems: Record<string, DataTableType>[]) => void;
}> = ({ displayData, data, tableData, onChange: handleChange, records }) => {
  const client = useApolloClient();
  const { formatMessage } = useIntl();
  const [token] = useSessionStorage('share_token');
  const {
    state: { isAuthenticated },
  } = useSession();
  const { palette } = useTheme();

  const { setLoader } = useThrottledLoader<
    GetLabResultResourcesQuery,
    GetRecordInput
  >();

  React.useEffect(() => {
    // For each lab result section, we want to load data using the following function
    setLoader(async (input: GetRecordInput) => {
      const res = await client.query<
        GetLabResultResourcesQuery,
        GetLabResultResourcesQueryVariables
      >({
        ...(!isAuthenticated && {
          context: {
            headers: {
              authorization: token,
            },
          },
        }),
        query: GetLabResultResourcesDocument,
        variables: {
          input,
        },
        fetchPolicy: 'no-cache',
      });
      return res.data;
    });
  }, [client, isAuthenticated, setLoader, token]);

  return (
    <Container maxWidth="md">
      <TableTitle>
        {/** Filter button should appear if patient has lab results */}
        {data.length > 0 && (
          <FilterButton
            fields={[
              {
                name: 'viewed',
                title: 'Viewed Status',
              },
              {
                name: 'date',
                title: 'Date',
                style: 'date',
              },
              {
                name: 'title',
                title: 'Title',
              },
            ]}
            items={tableData}
            onChange={(filteredItems) => {
              handleChange(filteredItems);
            }}
          />
        )}
        {displayData.length > 0 && <ConversionToggle group={LAB_TYPES} />}
      </TableTitle>
      {displayData.length === 0 ? (
        <NoRecordsWrapper>
          {formatMessage(messages.noRecordsFound)}
        </NoRecordsWrapper>
      ) : (
        <ProcessStepper
          circleColors={{
            active: palette.primary.main,
            inactive: palette.grey['900'],
            skipped: palette.grey['900'],
          }}
          currentStep={0}
          stepNames={records.map((record, index) => (
            <LabResultsSection key={`lab-results-${index}`} record={record} />
          ))}
        />
      )}
    </Container>
  );
};

export default LabResultCardView;
